import type { SearchResultV5 } from '@seek/chalice-types';
import throttle from 'lodash/throttle';
import { memo, useEffect, useRef, useState, useCallback } from 'react';
import hash from 'string-hash';

import type { TGooglePublisherTag } from './types';
import { loadScript, type LoadScriptOptions } from './utils';

export type TargetingData = NonNullable<SearchResultV5['gptTargeting']>;

const DEFAULT_DEBOUNCE = 500;
const DEFAULT_RESIZE_DEBOUNCE = 100;

const getTargetingHash = (targetingData?: TargetingData): string => {
  if (!targetingData) return '';

  const cleanedTargetingData = targetingData.filter(
    ({ value }) =>
      (Array.isArray(value) && value.length) ||
      (typeof value === 'string' && Boolean(value)),
  );

  return hash(JSON.stringify(cleanedTargetingData)).toString();
};

const useResizeAdListener = ({
  gpt,
  tagName,
  targetingData,
  responsiveSizing,
}: {
  gpt: TGooglePublisherTag | null;
  tagName: string;
  targetingData?: TargetingData;
  responsiveSizing?: GooglePublisherTagResponsiveSizing[];
}) => {
  const oldSize = useRef(window.innerWidth);
  const responsiveAdBreakpoints = responsiveSizing
    ?.filter((size) => size.minViewportWidth)
    .map((size) => Number(size.minViewportWidth));

  useEffect(() => {
    const throttleRefreshAd = throttle(refreshAd, DEFAULT_DEBOUNCE);

    const updateSize = () => {
      if (gpt && responsiveAdBreakpoints) {
        const shouldUpdate = responsiveAdBreakpoints.find((breakpoint) =>
          didSizeChange({
            newSize: window.innerWidth,
            breakpoint,
            oldSize: oldSize.current,
          }),
        );

        if (shouldUpdate) {
          throttleRefreshAd({
            gpt,
            tagName,
            targetingData,
          });
        }
      }
      oldSize.current = window.innerWidth;
    };

    const updateSizeThrottle = throttle(updateSize, DEFAULT_RESIZE_DEBOUNCE);

    if (gpt) {
      window.addEventListener('resize', updateSizeThrottle);
      return () => {
        window.removeEventListener('resize', updateSizeThrottle);
        throttleRefreshAd.cancel();
        updateSizeThrottle.cancel();
      };
    }
  }, [gpt, responsiveAdBreakpoints, targetingData, tagName]);
};

export type SingleSizeArray = [number, number];
const mapToSingleSizeArray = (sizes: GooglePublisherTagSize[]) => {
  // Using a set here to remove sizes duplicates
  const sizeGroups = sizes.reduce<Set<string>>(
    (allSizes, { height, width }) => {
      const sizeGroup = `${width}:${height}`;
      return allSizes.add(sizeGroup);
    },
    new Set(),
  );

  return Array.from(sizeGroups).map<SingleSizeArray>((size) => {
    const [width, height] = size.split(':').map(Number);
    return [width, height];
  });
};

interface ManageAdProps {
  gpt: TGooglePublisherTag;
  tagName: string;
}

interface didSizeChangeProps {
  newSize: number;
  oldSize: number | undefined;
  breakpoint: number;
}

export const didSizeChange = ({
  newSize,
  oldSize,
  breakpoint,
}: didSizeChangeProps): boolean => {
  if (!oldSize) {
    return false;
  }

  return (
    (oldSize < breakpoint && newSize > breakpoint) ||
    (oldSize > breakpoint && newSize < breakpoint)
  );
};

const getSlotByTagName = ({ gpt, tagName }: ManageAdProps) =>
  gpt
    .pubads()
    .getSlots()
    .find((s) => s.getSlotElementId() === tagName);

interface DefineSlotProps {
  gpt: TGooglePublisherTag;
  tagId: string;
  responsiveSizing: GooglePublisherTagResponsiveSizing[];
  tagName: string;
}

const defineSlot = ({
  gpt,
  tagId,
  responsiveSizing,
  tagName,
}: DefineSlotProps) => {
  const allSizes = responsiveSizing.reduce<GooglePublisherTagSize[]>(
    (acc, { sizes }) => acc.concat(sizes),
    [],
  );
  const sizeMap = mapToSingleSizeArray(allSizes);
  return gpt.defineSlot(tagId, sizeMap, tagName)?.addService(gpt.pubads());
};

interface MappingBuilderProps {
  gpt: TGooglePublisherTag;
  responsiveSizing: GooglePublisherTagResponsiveSizing[];
}

const mappingBuilder = ({ gpt, responsiveSizing }: MappingBuilderProps) => {
  const mapping = gpt.sizeMapping();
  responsiveSizing.map(({ minViewportWidth = 0, sizes }) => {
    const mappedResponsiveSizes = mapToSingleSizeArray(sizes);
    mapping.addSize([minViewportWidth, 0], mappedResponsiveSizes);
  });

  return mapping.build();
};

export const mappingBuilderForTest = mappingBuilder;

interface InitAdProps
  extends Omit<GooglePublisherTagProps, 'targetingData'>,
    ManageAdProps {}
export const initAd = ({
  gpt,
  tagName,
  tagId,
  responsiveSizing,
}: InitAdProps) => {
  gpt.cmd.push(() => {
    const slot = getSlotByTagName({
      gpt,
      tagName,
    });
    if (!slot) {
      const adSlot = defineSlot({ gpt, tagId, responsiveSizing, tagName });

      if (adSlot && responsiveSizing) {
        const mappingBuild = mappingBuilder({ gpt, responsiveSizing });
        if (mappingBuild) {
          adSlot.defineSizeMapping(mappingBuild);
        }
      }
      gpt.display(tagName);
    }
  });
};

interface RefreshAdProps
  extends Pick<GooglePublisherTagProps, 'targetingData'>,
    ManageAdProps {}
const refreshAd = ({ gpt, tagName, targetingData }: RefreshAdProps) => {
  gpt.cmd.push(() => {
    const slot = getSlotByTagName({
      gpt,
      tagName,
    });

    if (slot) {
      slot.clearTargeting();

      if (targetingData && Array.isArray(targetingData)) {
        targetingData.map(({ key, value }) => {
          slot.setTargeting(key, value);
        });
      }
      gpt.pubads().refresh([slot]);
    }
  });
};

interface GooglePublisherTagSize {
  width: number;
  height: number;
}

interface GooglePublisherTagResponsiveSizing {
  minViewportWidth?: number;
  sizes: GooglePublisherTagSize[];
}

interface GooglePublisherTagProps {
  tagId: string;
  responsiveSizing: GooglePublisherTagResponsiveSizing[];
  targetingData?: TargetingData;
  forceAdRefreshIneligiblity?: boolean;
  /**
   * The callback function to be invoked when an ad becomes viewable.
   * See: https://developers.google.com/publisher-tag/reference#googletag.events.ImpressionViewableEvent for more details
   */
  eventImpressionViewable?: LoadScriptOptions['eventImpressionViewable'];
}

interface UseIsAdEligibleOptions {
  fetchMarginPercent?: number; // mimic GPT fetchMarginPercent
  settleDelay?: number;
  forceAdRefreshIneligiblity?: boolean;
}

export const useIsAdEligibleForRefresh = (
  elementId: string,
  {
    fetchMarginPercent = 50,
    settleDelay = 200,
    forceAdRefreshIneligiblity = false,
  }: UseIsAdEligibleOptions = {},
): boolean => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [eligible, setEligible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computeRootMargin = useCallback(() => {
    const margin = Math.floor((window.innerHeight * fetchMarginPercent) / 100);
    return `${margin}px 0px ${margin}px 0px`;
  }, [fetchMarginPercent]);

  useEffect(() => {
    const el = document.getElementById(elementId);
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: computeRootMargin(),
        threshold: 0,
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [elementId, fetchMarginPercent, computeRootMargin]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    let cancelled = false;

    timerRef.current = setTimeout(() => {
      if (!forceAdRefreshIneligiblity && isIntersecting) {
        if (!cancelled) {
          setEligible(true);
        }
      } else {
        setEligible(false);
      }
    }, settleDelay);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [forceAdRefreshIneligiblity, isIntersecting, settleDelay]);

  return eligible;
};

const GooglePublisherTagView: React.FC<GooglePublisherTagProps> = (props) => {
  const {
    tagId,
    targetingData,
    responsiveSizing,
    eventImpressionViewable,
    forceAdRefreshIneligiblity,
  } = props;
  const [gpt, setGpt] = useState<TGooglePublisherTag | null>(null);
  const tagName = hash(tagId).toString();
  const isAdEligibleForRefresh = useIsAdEligibleForRefresh(tagName, {
    forceAdRefreshIneligiblity,
    fetchMarginPercent: 50,
    settleDelay: 500,
  });
  useResizeAdListener({ gpt, tagName, targetingData, responsiveSizing });
  const targetingHash = getTargetingHash(targetingData);
  const lastRefreshedTargeting = useRef<string | null>(null);

  useEffect(() => {
    const loadAndInit = async () => {
      const tag = await loadScript({
        eventImpressionViewable,
      });
      setGpt(tag);
      initAd({
        gpt: tag,
        tagName,
        tagId,
        responsiveSizing,
      });
    };
    loadAndInit();
  }, [tagId, tagName, responsiveSizing, eventImpressionViewable]);

  useEffect(() => {
    if (!gpt || !isAdEligibleForRefresh || !targetingData) return;

    // If we've already refreshed with this targetingData, do nothing
    if (lastRefreshedTargeting?.current === targetingHash) return;

    refreshAd({
      gpt,
      tagName,
      targetingData,
    });

    lastRefreshedTargeting.current = targetingHash;
  }, [gpt, isAdEligibleForRefresh, tagName, targetingData, targetingHash]);

  return <div data-automation={`ad-${tagId}`} id={tagName} />;
};

export const GooglePublisherTag = memo(GooglePublisherTagView);
